import { MAX_PIXELS } from '../limits';
import type { EditorDocument, GroupLayer, Layer, PaintLayer } from './types';
import { BLEND_MODES, SCHEMA_VERSION } from './types';

/**
 * Project file format (`.webpaint`) — gzip-compressed JSON written by the client.
 *
 * `format` is a magic marker so we can reject foreign JSON with a clear message,
 * and `version` drives the migration table below. Migrations are pure functions
 * from one raw shape to the next and run in sequence, so a v1 file will still
 * open after we ship v5.
 */

export const PROJECT_FORMAT = 'webpaint';

export interface ProjectFile {
  format: typeof PROJECT_FORMAT;
  version: number;
  savedAt: string;
  document: EditorDocument;
}

export class ProjectFormatError extends Error {
  readonly issues: string[];
  constructor(message: string, issues: string[] = []) {
    super(message);
    this.name = 'ProjectFormatError';
    this.issues = issues;
  }
}

export function serializeProject(doc: EditorDocument): string {
  const file: ProjectFile = {
    format: PROJECT_FORMAT,
    version: SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    document: doc,
  };
  return JSON.stringify(file);
}

/**
 * Migrations keyed by the version they upgrade FROM. Each returns the next
 * version's raw shape and must set `schemaVersion` to the version it produced —
 * not to `SCHEMA_VERSION`, or a two-step migration would skip the step in the
 * middle. Add one whenever SCHEMA_VERSION is bumped.
 *
 * v0 is the pre-versioning shape: no `schemaVersion`, and layers that may omit
 * any of the optional presentation fields. `normaliseDocument` fills the
 * defaults, so this step only has to make the version explicit.
 *
 * v1 → v2 adds layer masks. A v1 layer simply has none, and
 * `normaliseDocument` fills `mask: null`, so again the step is the version.
 *
 * v2 → v3 gives paint layers an explicit raster size (`naturalWidth` /
 * `naturalHeight`, matching `ImageLayer`). Until v3 every paint raster was
 * canvas-sized — that was an invariant nobody wrote down — so the migration
 * fills exactly that, and old files render pixel-for-pixel as they did.
 *
 * v3 → v4 introduces the layer tree: a layer may be a `group` with children.
 * No v3 document can contain a group, so there is nothing to rewrite — the
 * migration is the version, and the flat stack it carries is already a valid
 * tree.
 */
const migrations: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> = {
  0: (raw) => ({
    ...raw,
    schemaVersion: 1,
    layers: Array.isArray(raw.layers) ? raw.layers : [],
  }),
  1: (raw) => ({
    ...raw,
    schemaVersion: 2,
    layers: Array.isArray(raw.layers) ? raw.layers : [],
  }),
  2: (raw) => ({
    ...raw,
    schemaVersion: 3,
    layers: Array.isArray(raw.layers)
      ? raw.layers.map((layer) => {
          if (
            !layer ||
            typeof layer !== 'object' ||
            (layer as Record<string, unknown>).type !== 'paint'
          ) {
            return layer;
          }
          const paint = layer as Record<string, unknown>;
          return {
            ...paint,
            // The v2 invariant: paint rasters were the size of the canvas.
            naturalWidth: paint.naturalWidth ?? raw.width,
            naturalHeight: paint.naturalHeight ?? raw.height,
          };
        })
      : [],
  }),
  3: (raw) => ({
    ...raw,
    schemaVersion: 4,
    // A v3 stack is already a valid tree: groups are new, and nothing in a
    // v3 file can be one.
    layers: Array.isArray(raw.layers) ? raw.layers : [],
  }),
};

function runMigrations(raw: Record<string, unknown>, from: number): Record<string, unknown> {
  let current = raw;
  let version = from;
  while (version < SCHEMA_VERSION) {
    const step = migrations[version];
    if (!step) {
      throw new ProjectFormatError(
        `Project file is version ${version} and no migration exists to reach version ${SCHEMA_VERSION}.`,
      );
    }
    current = step(current);
    version += 1;
  }
  return current;
}

export function parseProject(json: string): EditorDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (cause) {
    throw new ProjectFormatError('Not a valid .webpaint file: JSON did not parse.', [
      cause instanceof Error ? cause.message : String(cause),
    ]);
  }
  return migrate(parsed);
}

export function migrate(input: unknown): EditorDocument {
  if (typeof input !== 'object' || input === null) {
    throw new ProjectFormatError('Not a valid .webpaint file: expected an object.');
  }
  const record = input as Record<string, unknown>;

  // Accept both a wrapped ProjectFile and a bare document (hand-written fixtures).
  const wrapped = record.format === PROJECT_FORMAT;
  if (record.format !== undefined && !wrapped) {
    throw new ProjectFormatError(`Not a webpaint project file (format: ${String(record.format)}).`);
  }
  const body = wrapped ? (record.document as unknown) : record;
  if (typeof body !== 'object' || body === null) {
    throw new ProjectFormatError('Not a valid .webpaint file: missing document.');
  }
  const bodyRecord = body as Record<string, unknown>;
  // A file with no version anywhere is a pre-versioning (v0) document, not a
  // current one — guessing "current" would silently misread old files.
  const version = Number(bodyRecord.schemaVersion ?? record.version ?? 0);
  if (!Number.isFinite(version) || version < 0) {
    throw new ProjectFormatError('Not a valid .webpaint file: missing schema version.');
  }
  if (version > SCHEMA_VERSION) {
    throw new ProjectFormatError(
      `This file was saved by a newer version of WebPaint (v${version} > v${SCHEMA_VERSION}).`,
    );
  }
  const migrated = version === SCHEMA_VERSION ? bodyRecord : runMigrations(bodyRecord, version);
  const result = validateDocument(migrated);
  if (!result.ok) throw new ProjectFormatError('Project file failed validation.', result.errors);
  return result.doc;
}

export type ValidationResult = { ok: true; doc: EditorDocument } | { ok: false; errors: string[] };

const LAYER_TYPES = new Set(['image', 'paint', 'shape', 'text', 'fill', 'adjustment', 'group']);
const BLEND_MODE_SET = new Set<string>(BLEND_MODES);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/**
 * Hand-rolled validation instead of a schema library: keeps the bundle small and
 * gives us precise, user-facing error strings.
 */
export function validateDocument(input: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isRecord(input)) return { ok: false, errors: ['document must be an object'] };

  const d = input;
  if (d.schemaVersion !== SCHEMA_VERSION) errors.push(`schemaVersion must be ${SCHEMA_VERSION}`);
  if (typeof d.id !== 'string' || !d.id) errors.push('id must be a non-empty string');
  if (typeof d.name !== 'string') errors.push('name must be a string');
  if (!Number.isInteger(d.width) || Number(d.width) < 1) errors.push('width must be a int >= 1');
  if (!Number.isInteger(d.height) || Number(d.height) < 1) errors.push('height must be a int >= 1');
  if (typeof d.background !== 'string') errors.push('background must be a string');
  if (!Array.isArray(d.layers)) errors.push('layers must be an array');
  // The 4096 px-per-side rule applies to project files too: a document that the
  // app could never allocate should be refused at the door, not at first render.
  if (
    Number.isInteger(d.width) &&
    Number.isInteger(d.height) &&
    Number(d.width) * Number(d.height) > MAX_PIXELS
  ) {
    errors.push(`width * height must not exceed ${MAX_PIXELS} pixels`);
  }

  if (Array.isArray(d.layers)) {
    validateLayers(d.layers, 'layers', errors, new Set<string>());
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, doc: normaliseDocument(d) };
}

/**
 * Validate a list of layers, and the lists nested inside groups.
 *
 * The path in the error message follows the tree (`layers[0].children[2]`), so
 * a broken file says *where* it is broken rather than just that it is.
 */
function validateLayers(
  rawLayers: unknown[],
  path: string,
  errors: string[],
  seen: Set<string>,
): void {
  rawLayers.forEach((raw: unknown, i: number) => {
    const at = `${path}[${i}]`;
    if (!isRecord(raw)) {
      errors.push(`${at} must be an object`);
      return;
    }
    if (typeof raw.type !== 'string' || !LAYER_TYPES.has(raw.type)) {
      errors.push(`${at}.type must be one of ${[...LAYER_TYPES].join(', ')}`);
    }
    if (typeof raw.id !== 'string' || !raw.id) errors.push(`${at}.id must be non-empty`);
    else if (seen.has(raw.id)) errors.push(`${at}.id is duplicated: ${raw.id}`);
    else seen.add(raw.id);
    const opacity = raw.opacity;
    // Optional fields are only validated when present: normaliseDocument fills
    // safe defaults, so requiring them here would reject valid files.
    if (
      opacity !== undefined &&
      (typeof opacity !== 'number' || Number.isNaN(opacity) || opacity < 0 || opacity > 1)
    ) {
      errors.push(`${at}.opacity must be between 0 and 1`);
    }
    if (raw.mask !== undefined && raw.mask !== null) {
      const message = maskIssue(raw.mask);
      if (message) errors.push(`${at}.mask ${message}`);
    }
    // A blend mode the canvas does not know is worse than an invalid one:
    // assigning it to globalCompositeOperation is silently ignored, leaving
    // the previous layer's operator active and corrupting everything under it.
    if (raw.blendMode !== undefined && !BLEND_MODE_SET.has(raw.blendMode as string)) {
      errors.push(`${at}.blendMode must be one of ${BLEND_MODES.join(', ')}`);
    }
    for (const key of ['scaleX', 'scaleY'] as const) {
      const value = raw[key];
      if (
        value !== undefined &&
        (typeof value !== 'number' || !Number.isFinite(value) || value <= 0)
      ) {
        errors.push(`${at}.${key} must be a positive, finite number`);
      }
    }
    // A paint layer's raster size drives mask baking, stroke targeting and
    // every bounds computation; a corrupt one would be invisible until it
    // mis-rendered, so it is checked whenever present.
    if (raw.type === 'paint') {
      for (const key of ['naturalWidth', 'naturalHeight'] as const) {
        const value = raw[key];
        if (
          value !== undefined &&
          (typeof value !== 'number' || !Number.isInteger(value) || value < 1)
        ) {
          errors.push(`${at}.${key} must be an integer >= 1`);
        }
      }
    }
    if (raw.angle !== undefined && (typeof raw.angle !== 'number' || !Number.isFinite(raw.angle))) {
      errors.push(`${at}.angle must be a finite number`);
    }
    // A group holds more layers, and those layers are validated the same way.
    if (raw.type === 'group') {
      if (!Array.isArray(raw.children)) {
        errors.push(`${at}.children must be an array`);
        return;
      }
      validateLayers(raw.children, `${at}.children`, errors, seen);
    }
  });
}

/** Why a `mask` field is unusable, or null when it is fine. */
function maskIssue(value: unknown): string | null {
  if (!isRecord(value)) return 'must be an object or null';
  if (typeof value.src !== 'string') return '.src must be a string';
  for (const flag of ['enabled', 'inverted'] as const) {
    // Absent flags are fine — normaliseDocument defaults them.
    if (value[flag] !== undefined && typeof value[flag] !== 'boolean') {
      return `.${flag} must be a boolean`;
    }
  }
  return null;
}

/**
 * Fill in optional/derived fields so the rest of the app can assume a fully
 * populated layer. Keeps validation cheap while making consumption safe.
 */
function normaliseDocument(d: Record<string, unknown>): EditorDocument {
  const width = Number(d.width);
  const height = Number(d.height);
  const layers = normaliseLayers(d.layers as unknown[], width, height);
  return {
    schemaVersion: SCHEMA_VERSION,
    id: String(d.id),
    name: String(d.name ?? 'Untitled'),
    width,
    height,
    background: String(d.background ?? '#ffffff'),
    layers,
    createdAt: String(d.createdAt ?? new Date().toISOString()),
    modifiedAt: String(d.modifiedAt ?? new Date().toISOString()),
  };
}

/** Normalise a list of layers, and the lists nested inside groups. */
function normaliseLayers(rawLayers: unknown[], width: number, height: number): Layer[] {
  if (!Array.isArray(rawLayers)) return [];
  return rawLayers.map((raw) => normaliseLayer(raw as Record<string, unknown>, width, height));
}

/** Fill in one layer's optional fields, and its children's. */
function normaliseLayer(layer: Record<string, unknown>, width: number, height: number): Layer {
  const merged = {
    visible: true,
    locked: false,
    opacity: 1,
    blendMode: 'normal',
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    clipping: false,
    mask: null,
    ...layer,
  } as unknown as Layer;
  // A mask carries its own defaults, and a hand-written file may name only the
  // pixels it drew.
  if (merged.mask) {
    const mask = merged.mask;
    merged.mask = {
      src: mask.src,
      enabled: mask.enabled ?? true,
      inverted: mask.inverted ?? false,
    };
  }
  // A paint layer without an explicit raster size is treated as canvas-sized —
  // the v2 invariant — so hand-written v3 fixtures keep working.
  if (merged.type === 'paint') {
    const paint = merged as PaintLayer;
    paint.naturalWidth = paint.naturalWidth ?? (Number(width) || 1);
    paint.naturalHeight = paint.naturalHeight ?? (Number(height) || 1);
  }
  if (merged.type === 'group') {
    const group = merged as GroupLayer;
    group.children = normaliseLayers(group.children as unknown[], width, height);
    // Since Iteration 14 the group composites for real, so its opacity and
    // blend mode are honest document fields and are kept as written. (They
    // were read-normalised to neutral in Iteration 11, when the renderer
    // could not apply them; old files only ever carry neutral values, so
    // dropping the normalisation changes nothing for them.)
  }
  return merged;
}
